import React, { useEffect, useMemo, useState } from "react";
import CustomSelect from "../../components/CustomSelect.jsx";
import {
    assignManagerTeamDriver,
    createManagerTeam,
    deleteManagerTeam,
    fetchManagerSeasonDriverOptions,
    fetchManagerSeriesSeasons,
    fetchManagerTeams,
    unassignManagerTeamDriver,
    updateManagerTeam,
} from "../../lib/managerApi.js";

const STATUS_OPTIONS = [
    { value: "active", label: "Aktiv" },
    { value: "inactive", label: "Inaktiv" },
];

const STATUS_FILTER_OPTIONS = [
    { value: "all", label: "Alle Teams" },
    { value: "active", label: "Nur aktiv" },
    { value: "inactive", label: "Nur inaktiv" },
];

const EMPTY_TEAM = {
    season_id: "",
    display_name: "",
    logo_url: "",
    website_url: "",
    status: "active",
    sort_order: "0",
};

function prettyError(err) {
    if (!err) return "Unbekannter Fehler";
    if (typeof err === "string") return err;
    if (err instanceof Error) return err.message || "Fehler";
    if (err.detail) return typeof err.detail === "string" ? err.detail : JSON.stringify(err.detail);
    return String(err);
}

function statusLabel(status) {
    return status === "inactive" ? "Inaktiv" : "Aktiv";
}

function StatusPill({ status }) {
    const inactive = status === "inactive";
    return (
        <span className={inactive ? "pill pill--danger" : "pill pill--am"}>
            {statusLabel(status)}
        </span>
    );
}

function Field({ label, children, hint = "" }) {
    return (
        <label style={{ display: "grid", gap: 6 }}>
            <span style={{ fontWeight: 800, fontSize: 13 }}>{label}</span>
            {children}
            {hint && <span className="muted" style={{ fontSize: 12 }}>{hint}</span>}
        </label>
    );
}

function driverLabel(entry) {
    const driver = entry?.driver;
    const name = entry?.driver_label || driver?.display_name || driver?.iracing_name || "Fahrer";
    const number = entry?.car_number ? `#${entry.car_number}` : "";
    const pos = entry?.grid_position ? `P${entry.grid_position}` : "";
    return [number, name, pos].filter(Boolean).join(" / ");
}

function teamGroupKey(team) {
    return team?.team_key || team?.display_name || "";
}

export default function TeamManagement() {
    const [seriesRows, setSeriesRows] = useState([]);
    const [seriesKey, setSeriesKey] = useState("gt3x");
    const [seasonId, setSeasonId] = useState("all");
    const [search, setSearch] = useState("");
    const [teamStatus, setTeamStatus] = useState("all");

    const [teams, setTeams] = useState([]);
    const [selectedTeamKey, setSelectedTeamKey] = useState("");
    const [selectedTeamSeasonId, setSelectedTeamSeasonId] = useState("");
    const [creatingTeam, setCreatingTeam] = useState(false);
    const [selectedSeason, setSelectedSeason] = useState(null);

    const [teamForm, setTeamForm] = useState(EMPTY_TEAM);
    const [driverOptions, setDriverOptions] = useState([]);
    const [driverSearch, setDriverSearch] = useState("");
    const [assignSeasonDriverId, setAssignSeasonDriverId] = useState("");

    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");

    const selectedSeries = useMemo(() => {
        return seriesRows.find((series) => series.series_key === seriesKey) || null;
    }, [seriesRows, seriesKey]);

    const seriesOptions = useMemo(() => {
        return seriesRows.map((series) => ({
            value: series.series_key,
            label: series.display_name,
        }));
    }, [seriesRows]);

    const seasonEditOptions = useMemo(() => {
        return (selectedSeries?.seasons || []).map((season) => ({
            value: season.id,
            label: `${season.display_name} (${season.status})`,
        }));
    }, [selectedSeries]);

    const seasonFilterOptions = useMemo(() => {
        return [
            { value: "all", label: "Alle Teams" },
            ...seasonEditOptions,
        ];
    }, [seasonEditOptions]);

    const selectedTeamGroup = useMemo(() => {
        return teams.find((team) => teamGroupKey(team) === selectedTeamKey) || null;
    }, [teams, selectedTeamKey]);

    const selectedTeamEntry = useMemo(() => {
        if (!selectedTeamGroup) return null;
        const entries = selectedTeamGroup.season_entries || [];
        return entries.find((entry) => String(entry.season_id) === String(selectedTeamSeasonId)) || entries[0] || null;
    }, [selectedTeamGroup, selectedTeamSeasonId]);

    const selectedTeamId = selectedTeamEntry?.id ? String(selectedTeamEntry.id) : "";
    const activeSeason = useMemo(() => {
        return selectedSeries?.seasons?.find((season) => season.status === "active") || selectedSeries?.seasons?.[0] || null;
    }, [selectedSeries]);
    const teamStatusFilterEnabled = seasonId === "all";
    const effectiveTeamStatus = teamStatusFilterEnabled ? teamStatus : "active";

    useEffect(() => {
        let active = true;

        async function loadSeries() {
            try {
                setLoading(true);
                setError("");
                const data = await fetchManagerSeriesSeasons();
                if (!active) return;

                const rows = Array.isArray(data.series) ? data.series : [];
                setSeriesRows(rows);
                const preferred = rows.find((series) => series.series_key === "gt3x") || rows[0];
                if (preferred) {
                    setSeriesKey((current) => current || preferred.series_key);
                    const preferredActiveSeason =
                        preferred.seasons?.find((season) => season.status === "active") ||
                        preferred.seasons?.[0];
                    setSeasonId(preferredActiveSeason ? String(preferredActiveSeason.id) : "all");
                }
            } catch (err) {
                if (active) setError(prettyError(err));
            } finally {
                if (active) setLoading(false);
            }
        }

        loadSeries();
        return () => {
            active = false;
        };
    }, []);

    useEffect(() => {
        const series = seriesRows.find((row) => row.series_key === seriesKey);
        if (!series) return;

        const hasCurrentSeason = seasonId === "all" || series.seasons?.some((season) => String(season.id) === String(seasonId));
        if (!hasCurrentSeason) setSeasonId(activeSeason ? String(activeSeason.id) : "all");

        setTeamForm((prev) => ({
            ...prev,
            season_id: prev.season_id || (activeSeason ? String(activeSeason.id) : ""),
        }));
    }, [seriesRows, seriesKey, seasonId, activeSeason]);

    async function loadTeams(preferredKey = selectedTeamKey) {
        if (!seriesKey) return;

        try {
            setLoading(true);
            setError("");
            const allSeasons = seasonId === "all";
            const data = await fetchManagerTeams({
                series_key: seriesKey,
                season_id: allSeasons ? "" : seasonId,
                all_seasons: allSeasons ? "true" : "",
                search,
                team_status: effectiveTeamStatus,
            });

            const rows = Array.isArray(data.teams) ? data.teams : [];
            setTeams(rows);
            setSelectedSeason(data.season || null);

            setSelectedTeamKey((current) => {
                if (preferredKey && rows.some((team) => teamGroupKey(team) === preferredKey)) return preferredKey;
                if (creatingTeam) return "";
                if (current && rows.some((team) => teamGroupKey(team) === current)) return current;
                return rows[0] ? teamGroupKey(rows[0]) : "";
            });
        } catch (err) {
            setError(prettyError(err));
            setTeams([]);
            setSelectedTeamKey("");
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        if (!seriesKey) return;
        loadTeams();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [seriesKey, seasonId, teamStatus]);

    useEffect(() => {
        if (creatingTeam) {
            setTeamForm({
                ...EMPTY_TEAM,
                season_id: activeSeason ? String(activeSeason.id) : "",
            });
            setSelectedTeamSeasonId(activeSeason ? String(activeSeason.id) : "");
            return;
        }

        if (!selectedTeamEntry) {
            setTeamForm(EMPTY_TEAM);
            return;
        }

        setSelectedTeamSeasonId((current) => current || String(selectedTeamEntry.season_id));
        setTeamForm({
            season_id: selectedTeamEntry.season_id ? String(selectedTeamEntry.season_id) : "",
            display_name: selectedTeamEntry.display_name || "",
            logo_url: selectedTeamEntry.logo_url || "",
            website_url: selectedTeamEntry.website_url || "",
            status: selectedTeamEntry.status || "active",
            sort_order: selectedTeamEntry.sort_order ? String(selectedTeamEntry.sort_order) : "0",
        });
    }, [selectedTeamEntry, creatingTeam, activeSeason]);

    useEffect(() => {
        const entries = selectedTeamGroup?.season_entries || [];
        if (!entries.length || creatingTeam) return;

        setSelectedTeamSeasonId((current) => {
            if (current && entries.some((entry) => String(entry.season_id) === String(current))) return current;
            const preferred = seasonId !== "all"
                ? entries.find((entry) => String(entry.season_id) === String(seasonId))
                : entries.find((entry) => entry.season?.status === "active") || entries[0];
            return preferred ? String(preferred.season_id) : "";
        });
    }, [selectedTeamGroup, creatingTeam, seasonId]);

    async function loadDriverOptions() {
        const seasonForDrivers = teamForm.season_id || selectedTeamEntry?.season_id;
        if (!seasonForDrivers) {
            setDriverOptions([]);
            return;
        }

        try {
            const data = await fetchManagerSeasonDriverOptions({
                season_id: seasonForDrivers,
                search: driverSearch,
                only_without_team: "false",
            });
            setDriverOptions(Array.isArray(data.season_drivers) ? data.season_drivers : []);
        } catch (err) {
            setError(prettyError(err));
            setDriverOptions([]);
        }
    }

    useEffect(() => {
        loadDriverOptions();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [teamForm.season_id, selectedTeamId]);

    function startCreateTeam() {
        setCreatingTeam(true);
        setSelectedTeamKey("");
        setSelectedTeamSeasonId(activeSeason ? String(activeSeason.id) : "");
        setSuccess("");
        setError("");
    }

    async function saveTeam() {
        if (!teamForm.display_name || !teamForm.season_id) {
            setError("Bitte Teamname und Saison auswaehlen.");
            return;
        }

        try {
            setBusy(true);
            setError("");
            setSuccess("");
            const payload = {
                season_id: Number(teamForm.season_id),
                display_name: teamForm.display_name,
                logo_url: teamForm.logo_url,
                website_url: teamForm.website_url,
                status: teamForm.status || "active",
                sort_order: Number(teamForm.sort_order || 0),
            };
            const data = creatingTeam || !selectedTeamEntry?.id
                ? await createManagerTeam(payload)
                : await updateManagerTeam(selectedTeamEntry.id, payload);

            const nextKey = data.team?.team_key || teamForm.display_name.trim().toLowerCase();
            setCreatingTeam(false);
            setSelectedTeamKey(nextKey);
            setSelectedTeamSeasonId(data.team?.season_id ? String(data.team.season_id) : String(teamForm.season_id));
            setSuccess(creatingTeam || !selectedTeamEntry?.id ? "Team wurde angelegt." : "Team wurde gespeichert.");
            await loadTeams(nextKey);
        } catch (err) {
            setError(prettyError(err));
        } finally {
            setBusy(false);
        }
    }

    async function removeTeam() {
        if (!selectedTeamEntry?.id) return;
        const name = selectedTeamEntry.display_name || "Team";
        if (!window.confirm(`Team '${name}' wirklich loeschen?`)) return;

        try {
            setBusy(true);
            setError("");
            setSuccess("");
            await deleteManagerTeam(selectedTeamEntry.id);
            setSuccess("Team wurde geloescht.");
            setSelectedTeamKey("");
            setSelectedTeamSeasonId("");
            await loadTeams();
        } catch (err) {
            setError(prettyError(err));
        } finally {
            setBusy(false);
        }
    }

    async function assignDriver() {
        if (!selectedTeamEntry?.id) {
            setError("Bitte Team zuerst als Team-Datensatz speichern.");
            return;
        }
        if (!assignSeasonDriverId) {
            setError("Bitte einen Saisonfahrer auswaehlen.");
            return;
        }

        try {
            setBusy(true);
            setError("");
            setSuccess("");
            await assignManagerTeamDriver(selectedTeamEntry.id, assignSeasonDriverId);
            setAssignSeasonDriverId("");
            setSuccess("Fahrer wurde dem Team zugeordnet.");
            await loadTeams(selectedTeamKey);
            await loadDriverOptions();
        } catch (err) {
            setError(prettyError(err));
        } finally {
            setBusy(false);
        }
    }

    async function unassignDriver(seasonDriverId) {
        if (!selectedTeamEntry?.id || !seasonDriverId) return;
        if (!window.confirm("Fahrer wirklich aus diesem Team entfernen?")) return;

        try {
            setBusy(true);
            setError("");
            setSuccess("");
            await unassignManagerTeamDriver(selectedTeamEntry.id, seasonDriverId);
            setSuccess("Fahrer wurde aus dem Team entfernt.");
            await loadTeams(selectedTeamKey);
            await loadDriverOptions();
        } catch (err) {
            setError(prettyError(err));
        } finally {
            setBusy(false);
        }
    }

    const teamSeasonOptions = useMemo(() => {
        return (selectedTeamGroup?.season_entries || []).map((entry) => ({
            value: entry.season_id,
            label: `${entry.season?.display_name || "Saison"} / ${entry.id ? "Team-Datensatz" : "nur Fahrer-Text"}`,
        }));
    }, [selectedTeamGroup]);

    const assignOptions = useMemo(() => {
        return driverOptions.map((entry) => ({
            value: entry.id,
            label: `${driverLabel(entry)}${entry.team_name ? ` / aktuell: ${entry.team_name}` : ""}`,
        }));
    }, [driverOptions]);

    return (
        <div className="rv-page manager-modern-page">
            <section className="rv-page-hero">
                <div className="rv-page-hero__inner">
                    <div>
                        <div className="rv-page-kicker">Liga Manager</div>
                        <h1 className="rv-page-title">Teamverwaltung</h1>
                        <p className="rv-page-subtitle">
                            Teams je Saison pflegen, Logos zuweisen und Saisonfahrer einem Team zuordnen.
                        </p>
                    </div>
                </div>
            </section>

            {error && <div className="rv-message rv-message--error">{error}</div>}
            {success && <div className="rv-message rv-message--ok">{success}</div>}

            <div className="card rv-filter-card" style={{ display: "grid", gap: 14 }}>
                <div
                    style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))",
                        gap: 12,
                    }}
                >
                    <Field label="Serie">
                        <CustomSelect value={seriesKey} onChange={setSeriesKey} options={seriesOptions} allowEmpty={false} />
                    </Field>

                    <Field label="Saison">
                        <CustomSelect
                            value={seasonId}
                            onChange={setSeasonId}
                            options={seasonFilterOptions}
                            allowEmpty={false}
                            disabled={!seasonFilterOptions.length}
                        />
                    </Field>

                    <Field
                        label="Teamstatus"
                        hint={!teamStatusFilterEnabled ? "Bei einzelner Saison werden immer nur aktive Teams angezeigt." : ""}
                    >
                        <CustomSelect
                            value={teamStatus}
                            onChange={setTeamStatus}
                            options={STATUS_FILTER_OPTIONS}
                            allowEmpty={false}
                            disabled={!teamStatusFilterEnabled}
                        />
                    </Field>

                    <Field label="Suche">
                        <input
                            className="input"
                            value={search}
                            onChange={(event) => setSearch(event.target.value)}
                            onKeyDown={(event) => {
                                if (event.key === "Enter") loadTeams();
                            }}
                            placeholder="Teamname ..."
                        />
                    </Field>
                </div>

                <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
                    <button className="btn btn--accent" type="button" onClick={() => loadTeams()} disabled={loading}>
                        {loading ? "Lade ..." : "Teams laden"}
                    </button>
                    <button className="btn" type="button" onClick={startCreateTeam} disabled={!seasonEditOptions.length}>
                        Neues Team
                    </button>
                    {selectedSeason ? (
                        <span className="muted">Aktuelle Auswahl: {selectedSeason.display_name} ({selectedSeason.status})</span>
                    ) : (
                        <span className="muted">Aktuelle Auswahl: Alle Teams dieser Serie</span>
                    )}
                </div>
            </div>

            <div
                style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 380px), 1fr))",
                    gap: 16,
                    alignItems: "start",
                }}
            >
                <div className="card" style={{ display: "grid", gap: 10 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 10 }}>
                        <h2 style={{ marginTop: 0 }}>Teams</h2>
                        <span className="pill pill--gesamt">{teams.length}</span>
                    </div>

                    <div style={{ display: "grid", gap: 8, maxHeight: 760, overflow: "auto", paddingRight: 4 }}>
                        {!teams.length && <div className="muted">Keine Teams gefunden.</div>}

                        {teams.map((team) => {
                            const key = teamGroupKey(team);
                            const active = key === selectedTeamKey;
                            const entries = team.season_entries || [];
                            const seasonText = entries
                                .map((entry) => `${entry.season?.display_name || "Saison"}: ${(entry.drivers || []).length} Fahrer`)
                                .join(" | ");
                            const drivers = team.team?.drivers || [];

                            return (
                                <button
                                    key={key}
                                    type="button"
                                    onClick={() => {
                                        setCreatingTeam(false);
                                        setSelectedTeamKey(key);
                                        setSuccess("");
                                        setError("");
                                    }}
                                    style={{
                                        textAlign: "left",
                                        border: active
                                            ? "1px solid rgba(228,254,60,.46)"
                                            : "1px solid rgba(255,255,255,.10)",
                                        background: active
                                            ? "rgba(228,254,60,.10)"
                                            : "rgba(255,255,255,.035)",
                                        borderRadius: 14,
                                        padding: 12,
                                        color: "var(--text)",
                                        cursor: "pointer",
                                        display: "grid",
                                        gap: 8,
                                    }}
                                >
                                    <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center" }}>
                                        <span style={{ display: "inline-flex", alignItems: "center", gap: 10, minWidth: 0 }}>
                                            {team.logo_url ? (
                                                <img
                                                    src={team.logo_url}
                                                    alt=""
                                                    style={{ width: 42, height: 28, objectFit: "contain" }}
                                                    loading="lazy"
                                                />
                                            ) : null}
                                            <strong>{team.display_name}</strong>
                                        </span>
                                        <StatusPill status={team.status} />
                                    </div>
                                    <div className="muted" style={{ fontSize: 13 }}>
                                        {seasonText || "Noch keiner Saison zugeordnet"}
                                    </div>
                                    <div className="muted" style={{ fontSize: 13 }}>
                                        Fahrer: {drivers.length ? drivers.map((entry) => entry.driver_label).join(", ") : "keine Fahrer"}
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                </div>

                <div className="card" style={{ display: "grid", gap: 18 }}>
                    {!selectedTeamGroup && !creatingTeam ? (
                        <div className="muted">Bitte ein Team auswaehlen oder ein neues Team anlegen.</div>
                    ) : (
                        <>
                            <div>
                                <div className="rv-page-kicker">{creatingTeam ? "Neues Team" : "Bearbeiten"}</div>
                                <h2 style={{ margin: "4px 0 0" }}>{creatingTeam ? "Team anlegen" : selectedTeamEntry?.display_name || "Team"}</h2>
                                {!creatingTeam && selectedTeamEntry?.id === null && (
                                    <p className="muted" style={{ marginTop: 6 }}>
                                        Dieses Team existiert aktuell nur als Text an Saisonfahrern. Speichern legt den Team-Datensatz an und verknuepft die Fahrer.
                                    </p>
                                )}
                            </div>

                            <section style={{ display: "grid", gap: 12 }}>
                                <h3 style={{ margin: 0 }}>Team-Basisdaten</h3>

                                {!creatingTeam && teamSeasonOptions.length > 1 && (
                                    <Field label="Saisonversion">
                                        <CustomSelect
                                            value={selectedTeamSeasonId}
                                            onChange={setSelectedTeamSeasonId}
                                            options={teamSeasonOptions}
                                            allowEmpty={false}
                                        />
                                    </Field>
                                )}

                                <div
                                    style={{
                                        display: "grid",
                                        gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 220px), 1fr))",
                                        gap: 12,
                                    }}
                                >
                                    <Field label="Saison">
                                        <CustomSelect
                                            value={teamForm.season_id}
                                            onChange={(value) => setTeamForm((prev) => ({ ...prev, season_id: value }))}
                                            options={seasonEditOptions}
                                            allowEmpty={false}
                                            disabled={!creatingTeam && Boolean(selectedTeamEntry?.id)}
                                        />
                                    </Field>

                                    <Field label="Teamname">
                                        <input
                                            className="input"
                                            value={teamForm.display_name}
                                            onChange={(event) => setTeamForm((prev) => ({ ...prev, display_name: event.target.value }))}
                                        />
                                    </Field>

                                    <Field label="Status">
                                        <CustomSelect
                                            value={teamForm.status}
                                            onChange={(value) => setTeamForm((prev) => ({ ...prev, status: value }))}
                                            options={STATUS_OPTIONS}
                                            allowEmpty={false}
                                        />
                                    </Field>

                                    <Field label="Sortierung">
                                        <input
                                            className="input"
                                            type="number"
                                            min="0"
                                            value={teamForm.sort_order}
                                            onChange={(event) => setTeamForm((prev) => ({ ...prev, sort_order: event.target.value }))}
                                        />
                                    </Field>
                                </div>

                                <Field label="Logo URL" hint="Wird in Results, Standings und Teamlisten als Teamlogo genutzt.">
                                    <input
                                        className="input"
                                        value={teamForm.logo_url}
                                        onChange={(event) => setTeamForm((prev) => ({ ...prev, logo_url: event.target.value }))}
                                        placeholder="https://..."
                                    />
                                </Field>

                                {teamForm.logo_url && (
                                    <div
                                        style={{
                                            border: "1px solid rgba(255,255,255,.10)",
                                            borderRadius: 14,
                                            padding: 12,
                                            background: "rgba(255,255,255,.035)",
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 12,
                                        }}
                                    >
                                        <img
                                            src={teamForm.logo_url}
                                            alt="Teamlogo Vorschau"
                                            style={{ maxWidth: 180, maxHeight: 72, objectFit: "contain" }}
                                        />
                                        <span className="muted">Logo-Vorschau</span>
                                    </div>
                                )}

                                <Field label="Website URL" hint="Optional, aktuell reine Stammdaten.">
                                    <input
                                        className="input"
                                        value={teamForm.website_url}
                                        onChange={(event) => setTeamForm((prev) => ({ ...prev, website_url: event.target.value }))}
                                        placeholder="Optional"
                                    />
                                </Field>

                                <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                                    <button className="btn btn--accent" type="button" onClick={saveTeam} disabled={busy}>
                                        {creatingTeam || !selectedTeamEntry?.id ? "Team anlegen" : "Team speichern"}
                                    </button>
                                    {!creatingTeam && selectedTeamEntry?.id && (
                                        <button className="btn" type="button" onClick={removeTeam} disabled={busy}>
                                            Team loeschen
                                        </button>
                                    )}
                                    {creatingTeam && (
                                        <button className="btn" type="button" onClick={() => setCreatingTeam(false)} disabled={busy}>
                                            Abbrechen
                                        </button>
                                    )}
                                </div>
                            </section>

                            <section style={{ display: "grid", gap: 12 }}>
                                <h3 style={{ margin: 0 }}>Fahrer im Team</h3>

                                {!selectedTeamEntry?.id ? (
                                    <div className="rv-message rv-message--error">
                                        Fahrer koennen erst zugeordnet werden, nachdem der Team-Datensatz gespeichert wurde.
                                    </div>
                                ) : (
                                    <>
                                        <div style={{ display: "grid", gap: 8 }}>
                                            {!(selectedTeamEntry.drivers || []).length && (
                                                <div className="muted">Noch keine Fahrer in dieser Saisonversion.</div>
                                            )}
                                            {(selectedTeamEntry.drivers || []).map((entry) => (
                                                <div
                                                    key={entry.id}
                                                    style={{
                                                        border: "1px solid rgba(255,255,255,.10)",
                                                        borderRadius: 14,
                                                        padding: 12,
                                                        background: "rgba(255,255,255,.035)",
                                                        display: "flex",
                                                        justifyContent: "space-between",
                                                        gap: 10,
                                                        alignItems: "center",
                                                        flexWrap: "wrap",
                                                    }}
                                                >
                                                    <div>
                                                        <strong>{driverLabel(entry)}</strong>
                                                        <div className="muted" style={{ fontSize: 13 }}>
                                                            {entry.class_name || "Klasse offen"} {entry.status ? `/ ${statusLabel(entry.status)}` : ""}
                                                        </div>
                                                    </div>
                                                    <button className="btn" type="button" onClick={() => unassignDriver(entry.id)} disabled={busy}>
                                                        Entfernen
                                                    </button>
                                                </div>
                                            ))}
                                        </div>

                                        <div
                                            style={{
                                                borderTop: "1px solid rgba(255,255,255,.10)",
                                                paddingTop: 12,
                                                display: "grid",
                                                gap: 12,
                                            }}
                                        >
                                            <Field label="Fahrer suchen">
                                                <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                                                    <input
                                                        className="input"
                                                        value={driverSearch}
                                                        onChange={(event) => setDriverSearch(event.target.value)}
                                                        onKeyDown={(event) => {
                                                            if (event.key === "Enter") loadDriverOptions();
                                                        }}
                                                        placeholder="Name, Startnummer, iRacing ID ..."
                                                        style={{ flex: "1 1 240px" }}
                                                    />
                                                    <button className="btn" type="button" onClick={loadDriverOptions} disabled={busy}>
                                                        Suchen
                                                    </button>
                                                </div>
                                            </Field>

                                            <Field label="Saisonfahrer zuweisen">
                                                <CustomSelect
                                                    value={assignSeasonDriverId}
                                                    onChange={setAssignSeasonDriverId}
                                                    options={assignOptions}
                                                    placeholder="Fahrer auswaehlen"
                                                />
                                            </Field>

                                            <div>
                                                <button className="btn btn--accent" type="button" onClick={assignDriver} disabled={busy || !assignSeasonDriverId}>
                                                    Fahrer dem Team zuweisen
                                                </button>
                                            </div>
                                        </div>
                                    </>
                                )}
                            </section>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
